import { createSelector } from 'reselect';

import { PatronLocationEntity } from 'components/patron-location/PatronLocation';
import { StoreType } from '@tock/shared/utils/connect';
import {
  allMetroAreasSelector,
  appPatronLocationSelector,
  getDetectedLocation,
} from 'selectors/appSelector';
import { core } from '@tock/coretypes';
import { getFeatures } from './featuresSelector';
import { map } from 'es-toolkit/compat';

export const getSelectedMetroArea = (state: StoreType) => state.metroArea.selectedMetroArea;

export interface CanonicalizedMetroArea {
  canonicalName: string;
  value: core.MetroArea;
}

export function canonicalize(term: string): string {
  return term.toLocaleLowerCase().replace(/\W/g, '');
}

export const metroAreasByCanonicalName = createSelector(
  allMetroAreasSelector,
  (metroAreas): CanonicalizedMetroArea[] => {
    return map(metroAreas, (metro) => {
      const canonicalName = canonicalize(metro.name ?? '');
      return {
        canonicalName,
        value: metro as core.MetroArea,
      };
    });
  }
);

/**
 * Exported for testing.
 */
export const nearbyMetro = (
  canonicalizedMetros: CanonicalizedMetroArea[],
  detectedLocation?: core.IDetectedLocation,
  patronLocation?: {
    initialized: boolean;
    history: PatronLocationEntity[];
  }
): core.MetroArea | undefined => {
  if (!patronLocation?.initialized) {
    return undefined;
  }
  if (patronLocation.history[0]?.metroAreaId) {
    const nearestMetro = canonicalizedMetros.find(
      ({ value: { id } }) => id === patronLocation.history[0]?.metroAreaId
    );
    if (nearestMetro) {
      return nearestMetro.value;
    }
  }

  if (!detectedLocation) {
    return undefined;
  }

  // If the server has told us what the nearby metro is, use that.
  if (detectedLocation.nearestMetroId) {
    const detectedNearestMetro = canonicalizedMetros.find(
      ({ value: { id } }) => id === detectedLocation.nearestMetroId
    );
    if (detectedNearestMetro) {
      return detectedNearestMetro.value;
    }
  }

  // Otherwise try to find a city with a matching name.
  if (detectedLocation.geonamesId) {
    const geonamesMatch = canonicalizedMetros.find(
      ({ value: { geonamesId } }) => geonamesId === detectedLocation.geonamesId
    )?.value;
    if (geonamesMatch) {
      return geonamesMatch;
    }
  }

  // If we can't find anything, fall back to matching canonicalized name.
  if (detectedLocation.city) {
    const canonicalTargetName = canonicalize(detectedLocation.city);
    const canonicalNameMatch = canonicalizedMetros.find(
      ({ canonicalName }) => canonicalName === canonicalTargetName
    )?.value;
    if (canonicalNameMatch) {
      return canonicalNameMatch;
    }
  }

  // And finally, give up.
  return undefined;
};

/**
 * Selects our best guess of the closest metro to the user, independent of what
 * the city picker has selected.
 */
export const getNearbyMetroSelector = createSelector(
  metroAreasByCanonicalName,
  getDetectedLocation,
  appPatronLocationSelector,
  getFeatures,
  nearbyMetro
);

export const currentMetroAreaOrUndefined = (
  selectedMetroArea?: core.MetroArea,
  nearbyMetro?: core.MetroArea
): core.MetroArea | undefined => {
  // If the user has selected something explicitly, always go with that.
  if (selectedMetroArea) {
    return selectedMetroArea;
  }

  // Otherwise pick the nearest metro, if available.
  if (nearbyMetro) {
    return nearbyMetro;
  }

  return undefined;
};

/**
 * exported for testing
 */
export const currentMetroArea = (
  canonicalizedMetros: CanonicalizedMetroArea[],
  selectedMetroArea?: core.MetroArea,
  nearbyMetro?: core.MetroArea
): core.MetroArea | undefined => {
  // If the user has selected something explicitly, always go with that.
  if (selectedMetroArea) {
    return selectedMetroArea;
  }

  // Otherwise pick the nearest metro, if available.
  if (nearbyMetro) {
    return nearbyMetro;
  }

  // If we still can't find anything, or if geolocation failed for some reason, fall back to Chicago.
  const chicago = canonicalize('Chicago');
  return canonicalizedMetros.find(
    (canonicalizedMetro) => canonicalizedMetro.canonicalName === chicago
  )?.value;
};

export const currentMetroOrUndefinedSelector = createSelector(
  getSelectedMetroArea,
  getNearbyMetroSelector,
  currentMetroAreaOrUndefined
);

export const currentMetroAreaSelector = createSelector(
  metroAreasByCanonicalName,
  getSelectedMetroArea,
  getNearbyMetroSelector,
  currentMetroArea
);

export const featuredCitiesSelector = createSelector(
  allMetroAreasSelector,
  currentMetroAreaSelector,
  (
    allMetroAreas: core.IMetroArea[],
    currentMetroArea: core.MetroArea | undefined
  ): core.MetroArea[] => {
    const featuredCities: core.IMetroArea[] = allMetroAreas.filter((area) => {
      return !!area.isFeatured || !!(currentMetroArea && area.name === currentMetroArea.name);
    });

    return (featuredCities ?? []) as core.MetroArea[];
  }
);
